import OrderableImagePlaceholderIcon from '@assets/icons/orderable-image-placeholder.svg';
import React from 'react';
import {
  CachedImage,
  CachedImageProps,
} from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { StyleProp, View, ViewStyle } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';

export interface CardItemProps {
  bottomContent?: React.ReactNode;
  bottomLeftElement?: React.ReactNode;
  bottomRightElement?: React.ReactNode;
  imageContainerStyle?: StyleProp<ViewStyle>;
  imageProps: CachedImageProps;
  style?: StyleProp<ViewStyle>;
  subheader?: string | React.ReactNode;
  title: string | React.ReactNode;
  topLeftElement?: React.ReactNode;
  topRightElement?: React.ReactNode;
}

export const CardItem: React.FC<CardItemProps> = ({
  imageProps,
  topLeftElement,
  topRightElement,
  bottomRightElement,
  bottomLeftElement,
  title,
  subheader,
  bottomContent,
  imageContainerStyle,
  style,
}) => {
  const [styles] = useStyles();
  const [hasImageError, setHasImageError] = React.useState(false);

  const { onError, source, style: imageStyle, ...restImageProps } = imageProps;

  const handleImageError: CachedImageProps['onError'] = (event) => {
    setHasImageError(true);
    onError?.(event);
  };

  const shouldRenderPlaceholder = hasImageError || !source?.uri;

  return (
    <View style={[styles.container, style]}>
      <View style={[styles.imageSection, imageContainerStyle]}>
        {shouldRenderPlaceholder ? (
          <View style={imageStyle}>
            <OrderableImagePlaceholderIcon height="100%" width="100%" />
          </View>
        ) : source ? (
          <CachedImage
            alt=""
            {...restImageProps}
            onError={handleImageError}
            source={source}
            style={[styles.image, imageStyle]}
          />
        ) : null}
        {topLeftElement || topRightElement ? (
          <View
            style={[
              styles.topOverlay,
              !topLeftElement ? styles.topOverlayRightOnly : null,
            ]}
          >
            {topLeftElement ? (
              <View style={styles.topLeftElement}>{topLeftElement}</View>
            ) : null}
            {topRightElement ? (
              <View style={styles.topRightElement}>{topRightElement}</View>
            ) : null}
          </View>
        ) : null}
        {bottomRightElement ? (
          <View style={styles.bottomRightElement}>{bottomRightElement}</View>
        ) : null}
        {bottomLeftElement ? (
          <View style={styles.bottomLeftElement}>{bottomLeftElement}</View>
        ) : null}
      </View>
      <View style={styles.contentSection}>
        {typeof title === 'string' ? (
          <Typography numberOfLines={1} variant="paragraph-base-600">
            {title}
          </Typography>
        ) : title ? (
          title
        ) : null}
        {typeof subheader === 'string' ? (
          <Typography numberOfLines={1} variant="paragraph-md">
            {subheader}
          </Typography>
        ) : subheader ? (
          subheader
        ) : null}
        {bottomContent}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  bottomLeftElement: {
    bottom: 8,
    left: 8,
    position: 'absolute',
    zIndex: 2,
  },
  bottomRightElement: {
    bottom: 8,
    position: 'absolute',
    right: 8,
    zIndex: 2,
  },
  container: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 8,
  },
  contentSection: {
    flex: 1,
    width: '100%',
  },
  image: {
    minWidth: '100%',
    width: '100%',
  },
  imageSection: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  topLeftElement: {
    flexShrink: 1,
    overflow: 'hidden',
  },
  topOverlay: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    left: 8,
    position: 'absolute',
    right: 8,
    top: 8,
    zIndex: 2,
  },
  topOverlayRightOnly: {
    justifyContent: 'flex-end',
  },
  topRightElement: {
    flexShrink: 0,
    marginLeft: 4,
  },
}));
